/**
 * Register-to-ZK-Devices Modal
 *
 * Two-step UX:
 *   1. Confirm: show what will be pushed to which devices.
 *   2. Result: show per-device outcome + Fingerprint Enrollment Helper.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Alert,
  Descriptions,
  List,
  Tag,
  Typography,
  Steps,
  Space,
  Divider,
  Spin,
  message
} from 'antd';
import {
  UserAddOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import { zkDeviceApi, zkRegisterApi, ZkDevice, RegisterEmployeeResult, DeviceRegisterResult } from '../../services/zkService';

const { Text, Paragraph } = Typography;

interface RegisterToZkModalProps {
  visible: boolean;
  onClose: () => void;
  employee: {
    id: string;
    employeeId: string;
    firstName: string;
    lastName: string;
  } | null;
}

const RegisterToZkModal: React.FC<RegisterToZkModalProps> = ({ visible, onClose, employee }) => {
  const [step, setStep] = useState<'confirm' | 'processing' | 'result'>('confirm');
  const [devices, setDevices] = useState<ZkDevice[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [result, setResult] = useState<RegisterEmployeeResult | null>(null);

  useEffect(() => {
    if (visible) {
      setStep('confirm');
      setResult(null);
      loadDevices();
    }
  }, [visible]);

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const data = await zkDeviceApi.getAll();
      setDevices(data.filter(d => d.isEnabled));
    } catch (e: any) {
      message.error('Failed to load ZK devices: ' + (e.message || e));
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleRegister = async () => {
    if (!employee) return;
    setStep('processing');
    try {
      const res = await zkRegisterApi.registerEmployee(employee.id);
      setResult(res);
      setStep('result');
      const okCount = res.devices.filter(d => d.status === 'ok').length;
      if (okCount === res.devices.length) {
        message.success(`Registered on all ${okCount} devices`);
      } else if (okCount > 0) {
        message.warning(`Registered on ${okCount}/${res.devices.length} devices`);
      } else {
        message.error('Registration failed on all devices');
      }
    } catch (e: any) {
      message.error(e.message || 'Registration failed');
      setStep('confirm');
    }
  };

  const fullName = employee ? `${employee.firstName} ${employee.lastName}`.trim() : '';
  const zkUserId = employee?.employeeId || '';
  const okDevices = result?.devices.filter(d => d.status === 'ok') || [];
  const failedDevices = result?.devices.filter(d => d.status === 'failed') || [];

  // Pick the device to recommend for fingerprint enrollment. The "Office"
  // device is the preferred starting point per client request (HR is always
  // staffed there, so new hires can drop in any time); other devices are
  // floor-specific and harder to coordinate. Fall back to the first
  // successful device when no Office device is present.
  const enrollmentDevice =
    okDevices.find(d => {
      const full = devices.find(x => x.id === d.deviceId);
      return /office/i.test(full?.deviceName || d.deviceName || '');
    }) || okDevices[0];
  const enrollmentDeviceFull = devices.find(d => d.id === enrollmentDevice?.deviceId);

  const renderDeviceStatus = (d: DeviceRegisterResult) => {
    if (d.status === 'ok') {
      return <Tag icon={<CheckCircleOutlined />} color="success">{d.action === 'updated' ? 'Updated' : 'Created'}</Tag>;
    }
    return <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
  };

  return (
    <Modal
      title={
        <Space>
          <UserAddOutlined />
          <span>Register to ZK Attendance Devices</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      maskClosable={step !== 'processing'}
      closable={step !== 'processing'}
      footer={
        step === 'confirm' ? [
          <Button key="cancel" onClick={onClose}>Cancel</Button>,
          <Button
            key="register"
            type="primary"
            icon={<UserAddOutlined />}
            loading={loadingDevices}
            disabled={!employee || devices.length === 0 || !zkUserId}
            onClick={handleRegister}
          >
            Register on {devices.length} Device{devices.length === 1 ? '' : 's'}
          </Button>
        ] : step === 'processing' ? null : [
          <Button key="done" type="primary" onClick={onClose}>Done</Button>
        ]
      }
    >
      <Steps
        size="small"
        current={step === 'confirm' ? 0 : step === 'processing' ? 1 : 2}
        items={[
          { title: 'Confirm' },
          { title: 'Pushing to Devices' },
          { title: 'Enrollment Helper' }
        ]}
        style={{ marginBottom: 24 }}
      />

      {step === 'confirm' && employee && (
        <>
          <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Employee">
              <Text strong>{fullName}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Employee ID">{employee.employeeId}</Descriptions.Item>
            <Descriptions.Item label="ZK User ID (auto)">
              <Text code>{zkUserId || <Text type="danger">Missing — set Employee ID first</Text>}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Display name on device">{fullName}</Descriptions.Item>
            <Descriptions.Item label="Privilege">Normal User</Descriptions.Item>
          </Descriptions>

          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Will push to {devices.length} active device{devices.length === 1 ? '' : 's'}:
          </Text>
          <Spin spinning={loadingDevices}>
            <List
              size="small"
              bordered
              dataSource={devices}
              renderItem={(d) => (
                <List.Item>
                  <Space>
                    <IdcardOutlined />
                    <Text strong>{d.deviceName}</Text>
                    <Text type="secondary">{d.ipAddress}:{d.port}</Text>
                  </Space>
                </List.Item>
              )}
              locale={{ emptyText: 'No active devices configured' }}
            />
          </Spin>

          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            message="What happens next"
            description={
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>User gets created/updated on each device (PIN + name)</li>
                <li>Existing employee mappings are preserved/linked automatically</li>
                <li>Fingerprint enrollment is NOT done yet — see next step after registering</li>
              </ul>
            }
          />
        </>
      )}

      {step === 'processing' && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Pushing to devices — this may take up to 30 seconds per device…</Paragraph>
        </div>
      )}

      {step === 'result' && result && (
        <>
          <Alert
            type={failedDevices.length === 0 ? 'success' : okDevices.length > 0 ? 'warning' : 'error'}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              failedDevices.length === 0
                ? `Registered successfully on all ${okDevices.length} device(s)`
                : okDevices.length > 0
                  ? `Registered on ${okDevices.length}/${result.devices.length} device(s) — ${failedDevices.length} failed`
                  : `Registration failed on all ${result.devices.length} device(s)`
            }
          />

          <List
            size="small"
            bordered
            dataSource={result.devices}
            renderItem={(d) => (
              <List.Item>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <Text strong>{d.deviceName}</Text>
                    {d.error && <Text type="danger" style={{ fontSize: 12 }}>— {d.error}</Text>}
                  </Space>
                  {renderDeviceStatus(d)}
                </Space>
              </List.Item>
            )}
            style={{ marginBottom: 16 }}
          />

          {okDevices.length > 0 && (
            <>
              <Divider orientation="left" plain>
                <Space>
                  <InfoCircleOutlined />
                  Fingerprint Enrollment Helper
                </Space>
              </Divider>

              <Alert
                type="info"
                showIcon={false}
                message={<Text strong>Next step: enroll fingerprint at the device</Text>}
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                      The user is registered but has <Text strong>no fingerprint</Text> yet. {fullName} needs to
                      physically visit a device <Text strong>one time</Text> (~30 seconds) to enroll.
                    </Paragraph>

                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Instructions</Text> (send to {fullName}):
                    </Paragraph>
                    <ol style={{ paddingLeft: 22, marginBottom: 8 }}>
                      <li>
                        Go to device{' '}
                        <Text strong>
                          {enrollmentDeviceFull?.deviceName || enrollmentDevice?.deviceName || '(any registered device)'}
                        </Text>
                        {enrollmentDeviceFull && (
                          <Text type="secondary"> ({enrollmentDeviceFull.ipAddress})</Text>
                        )}
                      </li>
                      <li>Press <Text code>Menu</Text> → <Text code>User Mgmt</Text> → <Text code>All Users</Text></li>
                      <li>
                        Enter admin password (ask HR), then find user{' '}
                        <Text code>{zkUserId}</Text> <Text type="secondary">({fullName})</Text>
                      </li>
                      <li>Select <Text code>Edit</Text> → <Text code>Fingerprint</Text></li>
                      <li>Place finger on sensor — repeat <Text strong>3 times</Text> when prompted</li>
                      <li>Done! Template is saved on device and syncs across devices on next sync.</li>
                    </ol>

                    <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                      Tip: menu labels may vary slightly by device model/firmware — look for "User" / "User Management" / "Enroll".
                    </Paragraph>
                  </div>
                }
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default RegisterToZkModal;
